<template>
    <div class="contract-page">
        <breadcrumbs
            class="pb-3"
            :text="breadCrumbText"
            :product-type="
                contract?.serviceType === 'ownExcursion' || contract?.serviceType === 'concert'
                    ? 'activities'
                    : contract?.serviceType
            "
        />
        <v-row>
            <v-col cols="12" md="12">
                <div class="page-title d-flex align-center">
                    <h1 class="me-2">{{ productName }}</h1>
                </div>
            </v-col>
        </v-row>
        <v-tabs v-model="productTab" color="primary">
            <v-tab v-if="hasAccess('show.contract.informationTab')" value="tab-information">
                {{ $t('information') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.descriptionTab', contract)" value="tab-description">
                {{ $t('description') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.photoTab', contract)" value="tab-photo">
                {{ $t('photo') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.pricesTab', contract)" value="tab-prices">
                {{ $t('prices') }}
            </v-tab>
            <v-tab
                v-if="hasAccess('show.contract.ratesAndAvailabilityTab', contract)"
                value="tab-rates_and_availability"
            >
                {{ $t('rates') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.availabilityTab')" value="tab-availability">
                {{ $t('availability') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.reservationsTab')" value="tab-reservations">
                {{ $t('reservations') }}
            </v-tab>
            <v-tab v-if="hasAccess('show.contract.BITab', contract)" value="tab-bi">
                <v-icon start icon="mdi-finance" /> {{ $t('bi') }}
            </v-tab>
            <v-tab v-if="isOwnHotel && contract?.id" value="tab-channel_manager_mapping">
                {{ $t('channel_manager_tariff_mapping') }}
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="productTab">
            <div>
                <v-tabs-window-item value="tab-information">
                    <SecondLevelTabs :tabs="secondLevelInfoTabs" translation-key="product_tabs">
                        <template v-slot:info>
                            <InfoContractForm
                                v-if="supplierStore.fullContract?.id"
                                :contract-id="contract?.id"
                                :service-type="contract?.serviceType"
                            />
                        </template>
                        <template v-slot:guests>
                            <InfoGuestsCategories :contract-id="contract?.id" />
                        </template>
                        <template v-slot:travel_categories>
                            <InfoGuestsCategories :contract-id="contract?.id" />
                        </template>
                        <template v-slot:drivers>
                            <InfoGuestsCategories :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:flight_number>
                            <InfoFlightNumber :contract-id="contract?.id" />
                        </template>
                        <template v-slot:schedule>
                            <InfoCharterSchedule :contract-id="contract?.id" />
                        </template>
                        <template v-slot:extra_services>
                            <InfoExtraServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:catalogue>
                            <InfoCatalogue :contract-id="contract?.id" />
                        </template>
                        <template v-slot:time_table>
                            <InfoTimetable :contract-id="contract?.id" />
                        </template>
                        <template v-slot:services>
                            <InfoServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:room_types>
                            <InfoServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:meal_types>
                            <InfoSecondaryServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:accommodation>
                            <InfoServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:transfer_schedule>
                            <InfoTransferTimeTable :contract-id="contract?.id" />
                        </template>
                        <template v-slot:board_types>
                            <InfoSecondaryServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:itinerary>
                            <InfoItinerary :contract-id="contract?.id" />
                        </template>
                        <template v-slot:duration>
                            <InfoSecondaryServices :contract-id="contract?.id" :service-type="contract?.serviceType" />
                        </template>
                        <template v-slot:itinerary_description>
                            <InfoItineraryDescription :contract-id="contract?.id" />
                        </template>
                        <template v-slot:coverage_options>
                            <InfoInsuranceServises :contract-id="contract?.id" />
                        </template>
                        <template v-slot:terms_of_ordering>
                            <InfoTermsOfOrderingForm
                                :contract-id="contract?.id"
                                :service-type="contract?.serviceType"
                            />
                        </template>
                    </SecondLevelTabs>
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-description">
                    <ProductHotelDescription v-if="contract?.serviceType === 'ownHotel' && contract?.id" />
                    <ProductTourDescription v-else-if="contract?.serviceType === 'tour' && contract?.id" />
                    <product-description-form v-else class="pt-4" />
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-photo">
                    <product-images-form :contract="contract" :service-type="contract?.serviceType" class="pt-4" />
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-prices">
                    <product-prices-form :contract="contract" />
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-rates_and_availability">
                    <!-- Trigger room type loading when Rates tab is accessed -->
                    <div style="display: none">{{ shouldLoadRoomTypes }}</div>
                    <div class="pa-4">
                        <template v-if="loaded">
                            <v-tabs v-model="availabilityTab" :mandatory="false" color="primary">
                                <v-tab value="none" class="d-none" />
                                <v-tab value="mass-edit-prices" :disabled="!isOwnHotel">
                                    {{ $t('supplier.mass_edit_prices') }}
                                </v-tab>
                                <!-- Hidden: Mass Edit for Availability subtab -->
                                <!-- <v-tab value="mass-edit-availability">
                                    {{ $t('supplier.mass_edit_availability') }}
                                </v-tab> -->
                                <v-tab v-if="isOwnHotel">
                                    {{ $t('supplier.open_close_rooms') }}
                                </v-tab>
                            </v-tabs>
                            <v-window
                                v-model="availabilityTab"
                                class="overflow-visible"
                                style="z-index: 1000; position: relative"
                            >
                                <v-window-item value="none" />
                                <v-window-item value="mass-edit-prices">
                                    <mass-edit-prices
                                        class="overflow-visible"
                                        :start-date="startDate"
                                        :end-date="endDate"
                                        :services="services"
                                        :tariffs="tariffs"
                                        @update="updateDailyRates"
                                    />
                                </v-window-item>
                                <!-- Hidden: Mass Edit for Availability window item -->
                                <!-- <v-window-item value="mass-edit-availability">
                                    <mass-edit-availability
                                        :start-date="startDate"
                                        :end-date="endDate"
                                        :services="servicesData"
                                        :contract="contract"
                                        @update="updateMultipleAvailabilities"
                                    />
                                </v-window-item> -->
                                <v-window-item key="tab-3">
                                    <open-close-rooms
                                        class="overflow-visible"
                                        :start-date="startDate"
                                        :end-date="endDate"
                                        :services="servicesData"
                                        :tariffs="tariffs"
                                        @update="updateMultipleAvailabilities"
                                    />
                                </v-window-item>
                            </v-window>
                        </template>
                        <template v-else>
                            <v-skeleton-loader width="100%" type="article, actions" />
                        </template>
                    </div>
                    <div class="pt-4 pb-0 mt-12">
                        <template v-if="loaded">
                            <calendar-filters
                                :services="services"
                                :start-date="startDate"
                                :end-date="endDate"
                                :contract="contract"
                                @update="filterCalendar"
                            />
                        </template>
                        <template v-else>
                            <v-skeleton-loader type="article" />
                        </template>
                    </div>
                    <v-row>
                        <v-col cols="12" md="12">
                            <div v-if="loadedData" style="overflow-x: auto; width: 100%">
                                <calendar-legend :contract="contract" />

                                <!-- For non-hotel services, use the original layout -->
                                <template v-if="!isOwnHotel">
                                    <table
                                        v-for="service in servicesData"
                                        v-show="!serviceId || serviceId === service.id"
                                        :key="service.id"
                                        class="mb-6 contract-availability-table full-width"
                                    >
                                        <thead>
                                            <tr class="service-name-row">
                                                <th class="pl-2 text-grey-darken-1" :colspan="service.rates.length + 1">
                                                    {{ service.name }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td class="td-header text-caption pl-2">
                                                    <span class="nowrap">{{
                                                        $filters.dateShortFormat(startDate)
                                                    }}</span>
                                                    -
                                                    <span class="nowrap">{{ $filters.dateShortFormat(endDate) }}</span>
                                                </td>
                                                <td v-for="day in service.rates" :key="service.id + '-' + day.date">
                                                    <div class="date-cell">
                                                        {{ $t(`supplier.days.${weekDay(day.date)}`) }}
                                                    </div>
                                                    <div class="date-cell-day">
                                                        {{ dayNumber(day.date) }}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="pl-2">
                                                    {{
                                                        $t(
                                                            isOwnHotel
                                                                ? 'supplier.room_status'
                                                                : 'supplier.service_status'
                                                        )
                                                    }}
                                                </td>
                                                <template
                                                    v-for="day in service.rates"
                                                    :key="'room-status-' + service.id + '-' + day.date"
                                                >
                                                    <room-status-cell
                                                        :date="day.date"
                                                        :service="day.service"
                                                        :closed="allTariffsClosed(service.tariffs, day.date)"
                                                        @update="updateAvailabilities"
                                                    />
                                                </template>
                                            </tr>
                                            <tr>
                                                <td class="pl-2">
                                                    {{
                                                        $t(
                                                            isOwnHotel
                                                                ? 'supplier.rooms_to_sell'
                                                                : 'supplier.services_to_sell'
                                                        )
                                                    }}
                                                </td>
                                                <template
                                                    v-for="day in service.rates"
                                                    :key="'rooms-to-sell-' + service.id + '-' + day.date"
                                                >
                                                    <room-to-sell-cell
                                                        :date="day.date"
                                                        :service="day.service"
                                                        @update="updateAvailabilities"
                                                    />
                                                </template>
                                            </tr>
                                        </tbody>

                                        <template v-if="isOwnHotel">
                                            <tariff-rows
                                                v-for="tariff in service.tariffs"
                                                :key="'tariff-' + service.id + '-' + tariff.id"
                                                :tariff="tariff"
                                                :service-id="service.id"
                                                :service-status="service.status || 'ACTIVE'"
                                                :sustainability-level="'BASIC'"
                                                @update="updateDailyRates"
                                            />
                                        </template>
                                        <tr v-else>
                                            <td class="pl-2">
                                                {{ $t('price') }}
                                            </td>
                                            <td
                                                v-for="day in service.rates"
                                                :key="`price-${day.date}`"
                                                class="cursor-default text-center"
                                            >
                                                {{ servicePriceAmount(service) }}
                                            </td>
                                        </tr>
                                    </table>
                                </template>

                                <!-- For hotel services, use grouped layout by basic room type -->
                                <template
                                    v-else-if="
                                        roomTypesLoaded && groupedServicesData && Array.isArray(groupedServicesData)
                                    "
                                >
                                    <template v-for="roomGroup in groupedServicesData" :key="roomGroup.basicRoomId">
                                        <div
                                            v-if="roomGroup && roomGroup.services && shouldShowRoomGroup(roomGroup)"
                                            class="basic-room-group-wrapper"
                                        >
                                            <!-- BASIC room table -->
                                            <template v-if="roomGroup.services && roomGroup.services.BASIC">
                                                <div class="sustainability-level-section basic-section">
                                                    <h4 class="sustainability-subtitle">
                                                        {{ $t('sustainability.basic').toUpperCase() }}
                                                    </h4>
                                                    <table
                                                        :key="roomGroup.services.BASIC.id"
                                                        class="mb-4 contract-availability-table full-width"
                                                    >
                                                        <thead>
                                                            <tr class="service-name-row">
                                                                <th
                                                                    class="pl-2 text-grey-darken-1"
                                                                    :colspan="roomGroup.services.BASIC.rates.length + 1"
                                                                >
                                                                    {{ roomGroup.services.BASIC.name }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td class="td-header text-caption pl-2">
                                                                    <span class="nowrap">{{
                                                                        $filters.dateShortFormat(startDate)
                                                                    }}</span>
                                                                    -
                                                                    <span class="nowrap">{{
                                                                        $filters.dateShortFormat(endDate)
                                                                    }}</span>
                                                                </td>
                                                                <td
                                                                    v-for="day in roomGroup.services.BASIC.rates"
                                                                    :key="roomGroup.services.BASIC.id + '-' + day.date"
                                                                >
                                                                    <div class="date-cell">
                                                                        {{ $t(`supplier.days.${weekDay(day.date)}`) }}
                                                                    </div>
                                                                    <div class="date-cell-day">
                                                                        {{ dayNumber(day.date) }}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td class="pl-2">
                                                                    {{ $t('supplier.room_status') }}
                                                                </td>
                                                                <template
                                                                    v-for="day in roomGroup.services.BASIC.rates"
                                                                    :key="
                                                                        'room-status-' +
                                                                        roomGroup.services.BASIC.id +
                                                                        '-' +
                                                                        day.date
                                                                    "
                                                                >
                                                                    <room-status-cell
                                                                        :date="day.date"
                                                                        :service="day.service"
                                                                        :closed="
                                                                            allTariffsClosed(
                                                                                roomGroup.services.BASIC.tariffs,
                                                                                day.date
                                                                            )
                                                                        "
                                                                        @update="updateAvailabilities"
                                                                    />
                                                                </template>
                                                            </tr>
                                                            <tr>
                                                                <td class="pl-2">
                                                                    {{ $t('supplier.rooms_to_sell') }}
                                                                </td>
                                                                <template
                                                                    v-for="day in roomGroup.services.BASIC.rates"
                                                                    :key="
                                                                        'rooms-to-sell-' +
                                                                        roomGroup.services.BASIC.id +
                                                                        '-' +
                                                                        day.date
                                                                    "
                                                                >
                                                                    <room-to-sell-cell
                                                                        :date="day.date"
                                                                        :service="day.service"
                                                                        @update="updateAvailabilities"
                                                                    />
                                                                </template>
                                                            </tr>
                                                        </tbody>

                                                        <tariff-rows
                                                            v-for="tariff in roomGroup.services.BASIC.tariffs"
                                                            :key="
                                                                'tariff-' +
                                                                roomGroup.services.BASIC.id +
                                                                '-' +
                                                                tariff.id
                                                            "
                                                            :tariff="tariff"
                                                            :service-id="roomGroup.services.BASIC.id"
                                                            :service-status="
                                                                roomGroup.services.BASIC.status || 'ACTIVE'
                                                            "
                                                            :sustainability-level="'BASIC'"
                                                            @update="updateDailyRates"
                                                        />
                                                    </table>
                                                </div>
                                            </template>

                                            <!-- SUSTAINABLE rooms section -->
                                            <template
                                                v-if="
                                                    roomGroup.services &&
                                                    roomGroup.services.SUSTAINABLE &&
                                                    roomGroup.services.SUSTAINABLE.length > 0
                                                "
                                            >
                                                <div class="sustainability-level-section sustainable-section">
                                                    <h4 class="sustainability-subtitle">
                                                        {{ $t('sustainability.sustainable').toUpperCase() }}
                                                    </h4>
                                                    <template
                                                        v-for="service in roomGroup.services.SUSTAINABLE"
                                                        :key="service.id"
                                                    >
                                                        <table class="mb-4 contract-availability-table full-width">
                                                            <thead>
                                                                <tr class="service-name-row">
                                                                    <th
                                                                        class="pl-2 text-grey-darken-1"
                                                                        :colspan="service.rates.length + 1"
                                                                    >
                                                                        {{ service.name }}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td class="td-header text-caption pl-2">
                                                                        <span class="nowrap">{{
                                                                            $filters.dateShortFormat(startDate)
                                                                        }}</span>
                                                                        -
                                                                        <span class="nowrap">{{
                                                                            $filters.dateShortFormat(endDate)
                                                                        }}</span>
                                                                    </td>
                                                                    <td
                                                                        v-for="day in service.rates"
                                                                        :key="service.id + '-' + day.date"
                                                                    >
                                                                        <div class="date-cell">
                                                                            {{
                                                                                $t(`supplier.days.${weekDay(day.date)}`)
                                                                            }}
                                                                        </div>
                                                                        <div class="date-cell-day">
                                                                            {{ dayNumber(day.date) }}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="pl-2">
                                                                        {{ $t('supplier.room_status') }}
                                                                    </td>
                                                                    <template
                                                                        v-for="day in service.rates"
                                                                        :key="
                                                                            'room-status-' + service.id + '-' + day.date
                                                                        "
                                                                    >
                                                                        <room-status-cell
                                                                            :date="day.date"
                                                                            :service="day.service"
                                                                            :closed="
                                                                                allTariffsClosed(
                                                                                    service.tariffs,
                                                                                    day.date
                                                                                )
                                                                            "
                                                                            @update="updateAvailabilities"
                                                                        />
                                                                    </template>
                                                                </tr>
                                                                <tr>
                                                                    <td class="pl-2">
                                                                        {{ $t('supplier.rooms_to_sell') }}
                                                                    </td>
                                                                    <template
                                                                        v-for="day in service.rates"
                                                                        :key="
                                                                            'rooms-to-sell-' +
                                                                            service.id +
                                                                            '-' +
                                                                            day.date
                                                                        "
                                                                    >
                                                                        <room-to-sell-cell
                                                                            :date="day.date"
                                                                            :service="day.service"
                                                                            @update="updateAvailabilities"
                                                                        />
                                                                    </template>
                                                                </tr>
                                                            </tbody>

                                                            <tariff-rows
                                                                v-for="tariff in service.tariffs"
                                                                :key="'tariff-' + service.id + '-' + tariff.id"
                                                                :tariff="tariff"
                                                                :service-id="service.id"
                                                                :service-status="service.status || 'ACTIVE'"
                                                                :sustainability-level="'SUSTAINABLE'"
                                                                @update="updateDailyRates"
                                                            />
                                                        </table>
                                                    </template>
                                                </div>
                                            </template>

                                            <!-- ZERO rooms section -->
                                            <template
                                                v-if="
                                                    roomGroup.services &&
                                                    roomGroup.services.ZERO &&
                                                    roomGroup.services.ZERO.length > 0
                                                "
                                            >
                                                <div class="sustainability-level-section zero-section">
                                                    <h4 class="sustainability-subtitle">
                                                        {{ $t('sustainability.zero').toUpperCase() }}
                                                    </h4>
                                                    <template
                                                        v-for="service in roomGroup.services.ZERO"
                                                        :key="service.id"
                                                    >
                                                        <table class="mb-4 contract-availability-table full-width">
                                                            <thead>
                                                                <tr class="service-name-row">
                                                                    <th
                                                                        class="pl-2 text-grey-darken-1"
                                                                        :colspan="service.rates.length + 1"
                                                                    >
                                                                        {{ service.name }}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td class="td-header text-caption pl-2">
                                                                        <span class="nowrap">{{
                                                                            $filters.dateShortFormat(startDate)
                                                                        }}</span>
                                                                        -
                                                                        <span class="nowrap">{{
                                                                            $filters.dateShortFormat(endDate)
                                                                        }}</span>
                                                                    </td>
                                                                    <td
                                                                        v-for="day in service.rates"
                                                                        :key="service.id + '-' + day.date"
                                                                    >
                                                                        <div class="date-cell">
                                                                            {{
                                                                                $t(`supplier.days.${weekDay(day.date)}`)
                                                                            }}
                                                                        </div>
                                                                        <div class="date-cell-day">
                                                                            {{ dayNumber(day.date) }}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="pl-2">
                                                                        {{ $t('supplier.room_status') }}
                                                                    </td>
                                                                    <template
                                                                        v-for="day in service.rates"
                                                                        :key="
                                                                            'room-status-' + service.id + '-' + day.date
                                                                        "
                                                                    >
                                                                        <room-status-cell
                                                                            :date="day.date"
                                                                            :service="day.service"
                                                                            :closed="
                                                                                allTariffsClosed(
                                                                                    service.tariffs,
                                                                                    day.date
                                                                                )
                                                                            "
                                                                            @update="updateAvailabilities"
                                                                        />
                                                                    </template>
                                                                </tr>
                                                                <tr>
                                                                    <td class="pl-2">
                                                                        {{ $t('supplier.rooms_to_sell') }}
                                                                    </td>
                                                                    <template
                                                                        v-for="day in service.rates"
                                                                        :key="
                                                                            'rooms-to-sell-' +
                                                                            service.id +
                                                                            '-' +
                                                                            day.date
                                                                        "
                                                                    >
                                                                        <room-to-sell-cell
                                                                            :date="day.date"
                                                                            :service="day.service"
                                                                            @update="updateAvailabilities"
                                                                        />
                                                                    </template>
                                                                </tr>
                                                            </tbody>

                                                            <tariff-rows
                                                                v-for="tariff in service.tariffs"
                                                                :key="'tariff-' + service.id + '-' + tariff.id"
                                                                :tariff="tariff"
                                                                :service-id="service.id"
                                                                :service-status="service.status || 'ACTIVE'"
                                                                :sustainability-level="'ZERO'"
                                                                @update="updateDailyRates"
                                                            />
                                                        </table>
                                                    </template>
                                                </div>
                                            </template>
                                        </div>
                                    </template>
                                </template>

                                <!-- Fallback: show ungrouped tables if room types not loaded or grouping failed -->
                                <template v-else>
                                    <table
                                        v-for="service in servicesData"
                                        v-show="!serviceId || serviceId === service.id"
                                        :key="service.id"
                                        class="mb-6 contract-availability-table full-width"
                                    >
                                        <thead>
                                            <tr class="service-name-row">
                                                <th class="pl-2 text-grey-darken-1" :colspan="service.rates.length + 1">
                                                    {{ service.name }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td class="td-header text-caption pl-2">
                                                    <span class="nowrap">{{
                                                        $filters.dateShortFormat(startDate)
                                                    }}</span>
                                                    -
                                                    <span class="nowrap">{{ $filters.dateShortFormat(endDate) }}</span>
                                                </td>
                                                <td v-for="day in service.rates" :key="service.id + '-' + day.date">
                                                    <div class="date-cell">
                                                        {{ $t(`supplier.days.${weekDay(day.date)}`) }}
                                                    </div>
                                                    <div class="date-cell-day">
                                                        {{ dayNumber(day.date) }}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="pl-2">
                                                    {{ $t('supplier.room_status') }}
                                                </td>
                                                <template
                                                    v-for="day in service.rates"
                                                    :key="'room-status-' + service.id + '-' + day.date"
                                                >
                                                    <room-status-cell
                                                        :date="day.date"
                                                        :service="day.service"
                                                        :closed="allTariffsClosed(service.tariffs, day.date)"
                                                        @update="updateAvailabilities"
                                                    />
                                                </template>
                                            </tr>
                                            <tr>
                                                <td class="pl-2">
                                                    {{ $t('supplier.rooms_to_sell') }}
                                                </td>
                                                <template
                                                    v-for="day in service.rates"
                                                    :key="'rooms-to-sell-' + service.id + '-' + day.date"
                                                >
                                                    <room-to-sell-cell
                                                        :date="day.date"
                                                        :service="day.service"
                                                        @update="updateAvailabilities"
                                                    />
                                                </template>
                                            </tr>
                                        </tbody>

                                        <tariff-rows
                                            v-for="tariff in service.tariffs"
                                            :key="'tariff-' + service.id + '-' + tariff.id"
                                            :tariff="tariff"
                                            :service-id="service.id"
                                            :service-status="service.status || 'ACTIVE'"
                                            :sustainability-level="'BASIC'"
                                            @update="updateDailyRates"
                                        />
                                    </table>
                                </template>
                            </div>
                            <template v-else>
                                <v-skeleton-loader transition="fade-transition" type="table" />
                            </template>
                        </v-col>
                    </v-row>
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-availability">
                    <OwnProductPackageAvailability
                        v-if="contract?.serviceType === 'customComplexTour'"
                        :contract="contract"
                    />
                    <OwnProductAvailability
                        v-else-if="productTab === 'tab-availability'"
                        :contract="contract"
                        :room-types="roomTypes"
                        :hotel-services="hotelServices"
                        :room-types-loaded="roomTypesLoaded"
                        class="mt-3"
                    />
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-reservations">
                    <orders-page ref="orders" product-page-mode>
                        <template v-slot:breadcrumbs><div /> </template>
                        <template v-slot:agentsPaymentsUpload><div /> </template>
                        <template v-slot:bookButtons>
                            <div class="d-flex align-center pb-4" />
                        </template>
                    </orders-page>
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-bi">
                    <product-b-i-form :contract="contract" />
                </v-tabs-window-item>
                <v-tabs-window-item value="tab-channel_manager_mapping" class="pa-4">
                    <ChannelManagerMappingTable :contract-id="contractId" :product-id="contractId" />
                </v-tabs-window-item>
            </div>
        </v-tabs-window>
    </div>
</template>

<script lang="ts">
    import {Component, Setup, Vue, Watch} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import RoomStatusCell from '~/components/supplier/RoomStatusCell.vue'
    import RoomToSellCell from '~/components/supplier/RoomToSellCell.vue'
    import TariffRows from '~src/components/supplier/TariffRows.vue'
    import MassEditPrices from '~src/components/supplier/MassEditPrices.vue'
    import ProductPricesForm from '~src/components/supplier/ProductPricesForm.vue'
    import ProductDescriptionForm from '@/components/supplier/ProductDescriptionForm.vue'
    import ProductImagesForm from '~src/components/supplier/ProductImagesForm.vue'
    import ProductBIForm from '@/components/supplier/ProductBIForm.vue'
    import CalendarFilters from '~src/components/supplier/CalendarFilters.vue'
    import OpenCloseRooms from '~src/components/supplier/OpenCloseRooms.vue'
    import {clone, timeout} from '@/utils/helpers'
    import {areAllTariffsClosed} from '~src/utils/daily-rate-status'
    import CalendarLegend from '@/components/supplier/CalendarLegend.vue'
    import LanguageLabel from '~/components/snippets/LanguageLabel.vue'
    import OrdersPage from '~/pages/account/OrdersPage.vue'
    import {orderFilters, ordersResponse as blankOrdersResponse} from '~/utils/store-blank-states'
    import {contractServiceType} from '~/utils/types/types'
    import {runtimeStore} from '~/composables/store-accessor'
    import InfoGuestsCategories from '~/components/products/information/InfoGuestsCategories.vue'
    import InfoContractForm from '~src/components/products/information/InfoContractForm.vue'
    import InfoFlightNumber from '~/components/products/information/InfoFlightNumber.vue'
    import InfoCharterSchedule from '~/components/products/information/InfoCharterSchedule.vue'
    import InfoExtraServices from '~src/components/products/information/InfoExtraServices.vue'
    import {useRecentViewsData} from '~/composables/recent-views'
    import InfoCatalogue from '~/components/products/information/InfoCatalogue.vue'
    import InfoTimetable from '~/components/products/information/InfoTimetable.vue'
    import InfoServices from '~src/components/products/information/InfoServices.vue'
    import InfoTransferTimeTable from '~/components/products/information/InfoTransferTimeTable.vue'
    import OwnProductAvailability from '~/components/products/OwnProductAvailability.vue'
    import InfoSecondaryServices from '~/components/products/information/InfoSecondaryServices.vue'
    import InfoItineraryDescription from '~/components/products/information/InfoItineraryDescription.vue'
    import InfoItinerary from '~/components/products/information/InfoItinerary.vue'
    import ProductHotelDescription from '~/components/products/hotels/ProductHotelDescription.vue'
    import InfoInsuranceServises from '~/components/products/information/InfoInsuranceServises.vue'
    import ProductTourDescription from '~/components/products/tours/ProductTourDescription.vue'
    import InfoTermsOfOrderingForm from '~/components/products/information/InfoTermsOfOrderingForm.vue'
    import ChannelManagerMappingTable from '~src/components/products/information/ChannelManagerMappingTable.vue'
    import OwnProductPackageAvailability from '~/components/products/OwnProductPackageAvailability.vue'

    @Component({
        components: {
            OwnProductPackageAvailability,
            InfoTermsOfOrderingForm,
            ProductTourDescription,
            InfoInsuranceServises,
            ProductHotelDescription,
            InfoItinerary,
            InfoItineraryDescription,
            InfoSecondaryServices,
            InfoTransferTimeTable,
            OwnProductAvailability,
            InfoServices,
            InfoTimetable,
            InfoCatalogue,
            InfoExtraServices,
            InfoCharterSchedule,
            InfoFlightNumber,
            InfoContractForm,
            InfoGuestsCategories,
            OrdersPage,
            LanguageLabel,
            CalendarLegend,
            OpenCloseRooms,
            CalendarFilters,
            // MassEditAvailability: defineAsyncComponent(() => import('@/components/supplier/MassEditAvailability.vue')), // Hidden
            MassEditPrices,
            ProductDescriptionForm,
            ProductImagesForm,
            ProductBIForm,
            TariffRows,
            Breadcrumbs,
            RoomStatusCell,
            RoomToSellCell,
            ProductPricesForm,
            ChannelManagerMappingTable,
        },
    })
    export class ContractPage extends Vue {
        @Setup(() => useAccess().hasAccess) hasAccess: HasAccessFunction

        contractId = null
        startDate = null
        endDate = null
        loaded = false
        loadedData = false
        availabilityTab = 'none'
        serviceId = 0
        tempFilters = null

        productTab = null
        isInitialMount = true

        priceLists = []
        roomTypes = []
        hotelServices = []
        roomTypesLoaded = false

        created() {
            this.contractId = this.$route.params.contractId
            if (this.contractId) {
                this.tempFilters = persistentStore.orderFilters
                persistentStore.CLEAR_ORDER_FILTERS()
                runtimeStore.SET_ORDERS_RESPONSE(blankOrdersResponse())
                runtimeStore.SET_LOADING(true)
                const contractIdNum = parseInt(this.contractId, 10)
                if (!isNaN(contractIdNum)) {
                    this.setProductIdFilter(contractIdNum)
                }
            }
        }

        @Watch('productTab')
        async onProductTabChange(newTab: string, oldTab: string) {
            if (this.isInitialMount) {
                return
            }
            if (newTab === 'tab-rates_and_availability' || newTab === 'tab-availability') {
                this.roomTypesLoaded = false
                await this.load()
                await this.loadRoomTypeDataOnce()
            }
            if (newTab === 'tab-reservations') {
                if (!this.contract?.id && this.contractId) {
                    await supplierStore.loadContract(this.contractId)
                    await supplierStore.loadFullContract()
                }
                if (this.contract?.id) {
                    this.setProductIdFilter(this.contract.id)
                    await this.reloadOrders()
                }
            }
        }

        @Watch('contract.id', {immediate: false})
        async onContractIdChange(newContractId: number, oldContractId: number) {
            if (newContractId && newContractId !== oldContractId && this.productTab === 'tab-reservations') {
                this.setProductIdFilter(newContractId)
                await this.reloadOrders()
            }
        }

        // Load room type data when switching to Rates tab
        get shouldLoadRoomTypes() {
            if (this.productTab === 'tab-rates_and_availability' && this.isOwnHotel && !this.roomTypesLoaded) {
                // Trigger loading in next tick to avoid infinite loops
                this.$nextTick(() => {
                    this.loadRoomTypeDataOnce()
                })
            }
            return this.productTab
        }

        async mounted() {
            if (this.$route?.query?.productTab) {
                this.productTab = this.$route?.query?.productTab
                this.$router.replace({query: {}})
            } else if (this.$conf.public.account.productsManagementForSuppliersHotelsRAOnly && authStore.isSupplier) {
                this.productTab = 'tab-rates_and_availability'
            }
            const startDate = this.$dateFns.addDays(new Date(), 1)
            this.startDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000)
                .toISOString()
                .split('T')[0]

            const endDate = this.$dateFns.addDays(new Date(), 1)
            endDate.setMonth(endDate.getMonth() + 1)
            this.endDate = endDate.toISOString().split('T')[0]

            await supplierStore.loadContract(this.contractId)
            await supplierStore.loadFullContract()

            if (this.contract?.id) {
                const currentProductId = persistentStore.orderFilters.productId
                if (currentProductId !== this.contract.id) {
                    this.setProductIdFilter(this.contract.id)
                    if (this.productTab === 'tab-reservations') {
                        await this.reloadOrders()
                    }
                }
            }

            await this.load()
            if (!supplierStore.internalSuppliers.length) await supplierStore.loadSuppliersForContract()

            // Load room type data once for caching (only for hotels)
            await this.loadRoomTypeDataOnce()

            this.loaded = true
            this.loadedData = true
            await this.$nextTick()
            this.isInitialMount = false
        }

        beforeUnmount() {
            supplierStore.resetDataDependsOnContractId()

            if (this.contractId) {
                const contractIdNum = parseInt(this.contractId, 10)
                if (!isNaN(contractIdNum) && persistentStore.orderFilters.productId === contractIdNum) {
                    persistentStore.SET_ORDER_FILTER({key: 'productId', val: null})
                    persistentStore.SET_ORDER_FILTER({key: 'filterServices', val: false})
                }
            }

            persistentStore.SET_ORDER_FILTERS(
                Object.assign({}, this.tempFilters, {
                    serviceName: orderFilters()['productId'],
                    filterServices: orderFilters()['filterServices'],
                })
            )
        }

        setProductIdFilter(productId: number) {
            if (persistentStore.orderFilters.productId !== productId) {
                persistentStore.SET_ORDER_FILTERS(
                    Object.assign({}, persistentStore.orderFilters, {
                        productId,
                        filterServices: true,
                    })
                )
            }
        }

        getOrdersComponent() {
            const ordersPage = this.$refs.orders as any
            return ordersPage?.$refs?.orders
        }

        async reloadOrders() {
            runtimeStore.SET_ORDERS_RESPONSE(blankOrdersResponse())
            runtimeStore.SET_LOADING(true)
            await this.$nextTick()
            const ordersComponent = this.getOrdersComponent()
            if (ordersComponent) {
                await ordersComponent.filter()
            }
        }

        async load() {
            const rq = {contractId: this.contractId, dateFrom: this.startDate, dateTo: this.endDate}

            if (this.contract) {
                const $route = useRoute()
                const linkName = this.contract.name
                const serviceType = this.contract.serviceType
                await useRecentViewsData($route, linkName, serviceType)
            }

            await Promise.all([
                supplierStore.loadAvailability(rq),
                supplierStore.loadDailyRates(rq),
                this.isOwnHotel ? supplierStore.loadPrices({contractId: this.contractId}) : this.ownProductPriceLists(),
            ])
        }

        /**
         * Load room type data only once when needed
         */
        async loadRoomTypeDataOnce() {
            if (this.isOwnHotel && !this.roomTypesLoaded) {
                await this.loadRoomTypeData()
            }
        }

        async ownProductPriceLists() {
            try {
                this.priceLists = (await this.$api.ownProductPriceLists.get({contractId: this.contractId})).priceLists
            } catch (e) {
                this.priceLists = []
            }
        }

        servicePriceAmount(service) {
            return this.priceLists[0]?.prices.find(({serviceId}) => serviceId === service.id)?.amount
        }

        /**
         * Load room type data with sustainability levels and service-to-room-type mapping
         * This is called only once when the component mounts or when the tab is opened
         */
        async loadRoomTypeData() {
            // Skip if already loaded to prevent unnecessary API calls
            if (this.roomTypesLoaded) {
                return
            }

            try {
                this.roomTypesLoaded = false

                // Get hotel ID from supplier store
                const hotelId = supplierStore.hotelInfo?.hotelId
                const contractId = this.contractId

                if (!hotelId || !contractId) {
                    this.roomTypesLoaded = true
                    return
                }

                // Load hotel services to get service-to-room-type mapping
                const ownHotelServices = await this.$api.ownHotelServices.get({contractId})

                // Load room types from hotel description
                const roomTypesResponse = await this.$api.hotelDescriptionRoomType.get(hotelId, {
                    locale: 'all',
                })

                this.roomTypes = roomTypesResponse?.hotelRoomTypes || []
                this.hotelServices = ownHotelServices?.services || []
            } catch (error) {
                console.error('Error loading room type data:', error)
                this.roomTypes = []
                this.hotelServices = []
            } finally {
                this.roomTypesLoaded = true
            }
        }

        /**
         * Get room type by service ID using the service-to-room-type mapping
         */
        getRoomTypeByServiceId(serviceId) {
            // First try to find the service in hotel services to get the room type ID
            const hotelService = this.hotelServices.find(service => service.id === serviceId)
            if (hotelService && hotelService.hotelRoomTypeId) {
                // Use the hotelRoomTypeId from hotel service to find the room type
                const roomType = this.roomTypes.find(rt => rt.id === hotelService.hotelRoomTypeId)
                if (roomType) {
                    return roomType
                }
            }

            // Fallback: try direct serviceId match with room type ID
            const directMatch = this.roomTypes.find(rt => rt.id === serviceId)
            if (directMatch) {
                return directMatch
            }

            return null
        }

        get locales() {
            return Object.values(this.$conf.public.locales)
        }

        get services() {
            return supplierStore.services || []
        }

        get tariffs() {
            return supplierStore.tariffs || []
        }

        isSpecialRoomService(serviceId) {
            const sustainabilityLevel = this.getRoomTypeByServiceId(serviceId)?.sustainabilityLevel
            return !!sustainabilityLevel && sustainabilityLevel !== 'BASIC'
        }

        allTariffsClosed(tariffs, date) {
            return areAllTariffsClosed(tariffs, date)
        }

        get servicesData() {
            if (!this.loadedData) return []
            let result = supplierStore.services.map(service => {
                let availabilitiesByService = supplierStore.availabilityServices.filter(availabilityService => {
                    return availabilityService.serviceId === service.id
                })

                let rates = this.daysArray.map(day => {
                    let serviceByDate = availabilitiesByService.filter(availabilityService => {
                        return (
                            Date.parse(availabilityService.dateFrom) <= Date.parse(day) &&
                            Date.parse(availabilityService.dateTo) >= Date.parse(day)
                        )
                    })

                    return {
                        date: day,
                        service: serviceByDate[0],
                    }
                })

                let tariffs = supplierStore.tariffs.map(tariff => {
                    let dailyRatesForTariff = this.daysArray.map(tariffDay => {
                        let dailyRateForTariff = supplierStore.dailyRates.filter(dailyRate => {
                            return (
                                dailyRate.date === tariffDay &&
                                dailyRate.serviceId === service.id &&
                                dailyRate.tariffId === tariff.id
                            )
                        })

                        return {
                            date: tariffDay,
                            rate: dailyRateForTariff.length
                                ? dailyRateForTariff[dailyRateForTariff.length - 1]
                                : {
                                      placeholder: true,
                                      active: false,
                                      serviceId: service.id,
                                      tariffId: tariff.id,
                                  },
                        }
                    })

                    return {
                        id: tariff.id,
                        name: tariff.name,
                        dailyRates: dailyRatesForTariff,
                    }
                })
                return {
                    id: service.id,
                    name: service.name,
                    categoryId: rates[0].service.categoryId,
                    rates: rates,
                    tariffs: tariffs,
                }
            })

            return result
        }

        /**
         * Group services by basic room type, with each group containing BASIC + its SUSTAINABLE/ZERO variants
         */
        get groupedServicesData() {
            if (!this.isOwnHotel || !this.roomTypesLoaded) {
                return this.servicesData
            }

            const basicRoomGroups = new Map()

            // First, organize services by their basic room ID
            this.servicesData.forEach(service => {
                const roomType = this.getRoomTypeByServiceId(service.id)

                if (!roomType) {
                    // If no room type found, treat as standalone BASIC room with unique group
                    const basicRoomId = `standalone_${service.id}`
                    if (!basicRoomGroups.has(basicRoomId)) {
                        basicRoomGroups.set(basicRoomId, {
                            basicRoomId,
                            basicRoomName: service.name,
                            services: {
                                BASIC: service,
                                SUSTAINABLE: [],
                                ZERO: [],
                            },
                        })
                    }
                    return
                }

                // ZSH-873: hide disabled (inactive) room types from the Rates tab
                if (roomType.active === false) {
                    return
                }

                const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
                let basicRoomId

                if (sustainabilityLevel === 'BASIC') {
                    basicRoomId = roomType.id
                } else {
                    // For SUSTAINABLE/ZERO rooms, find their basic room ID
                    basicRoomId = roomType.basicRoomId

                    if (!basicRoomId) {
                        // If no basicRoomId, treat as standalone
                        const standaloneId = `standalone_${service.id}`
                        basicRoomGroups.set(standaloneId, {
                            basicRoomId: standaloneId,
                            basicRoomName: service.name,
                            services: {
                                BASIC: null,
                                SUSTAINABLE: sustainabilityLevel === 'SUSTAINABLE' ? [service] : [],
                                ZERO: sustainabilityLevel === 'ZERO' ? [service] : [],
                            },
                        })
                        return
                    }
                }

                if (!basicRoomGroups.has(basicRoomId)) {
                    basicRoomGroups.set(basicRoomId, {
                        basicRoomId,
                        basicRoomName: '',
                        services: {
                            BASIC: null,
                            SUSTAINABLE: [],
                            ZERO: [],
                        },
                    })
                }

                const group = basicRoomGroups.get(basicRoomId)

                if (sustainabilityLevel === 'BASIC') {
                    group.services.BASIC = service
                    group.basicRoomName = service.name
                } else if (sustainabilityLevel === 'SUSTAINABLE') {
                    group.services.SUSTAINABLE.push(service)
                } else if (sustainabilityLevel === 'ZERO') {
                    group.services.ZERO.push(service)
                }
            })

            // Convert to array, filter out groups without services, and sort by basic room name
            const result = Array.from(basicRoomGroups.values())
                .filter(
                    group =>
                        group.services.BASIC || group.services.SUSTAINABLE.length > 0 || group.services.ZERO.length > 0
                )
                .sort((a, b) => a.basicRoomName.localeCompare(b.basicRoomName))

            return result
        }

        /**
         * Determine if a room group should be displayed based on the current service filter
         */
        shouldShowRoomGroup(roomGroup) {
            if (!this.serviceId) {
                // Show all groups when no specific service is selected
                return true
            }

            // Check if the selected service is in any of the sustainability levels of this group
            const hasBasic = roomGroup.services.BASIC && roomGroup.services.BASIC.id === this.serviceId
            const hasSustainable =
                roomGroup.services.SUSTAINABLE && roomGroup.services.SUSTAINABLE.some(s => s.id === this.serviceId)
            const hasZero = roomGroup.services.ZERO && roomGroup.services.ZERO.some(s => s.id === this.serviceId)

            return hasBasic || hasSustainable || hasZero
        }

        async filterCalendar(rq) {
            this.loadedData = false

            this.startDate = rq.dateFrom
            this.endDate = rq.dateTo
            this.serviceId = rq.serviceId

            await this.load()

            this.loadedData = true
        }

        get hasBusinessIntelligenceAccess() {
            const productAvailable = ['ownExcursion', 'ownCharter', 'ownTransfer'].includes(this.contract?.serviceType)

            return (
                productAvailable &&
                this.$conf.public.account.hasBusinessIntelligenceAccess &&
                this.isB2B &&
                ['supervisor', 'director', 'support'].includes(this.personRole)
            )
        }

        get personRole() {
            return authStore.personRole
        }

        get isB2B() {
            return authStore.hasCorporateAccess
        }

        get daysArray() {
            const addDays = (date, days = 1) => {
                const result = new Date(date)
                result.setDate(result.getDate() + days)
                return result
            }

            const dateRange = (start, end, range = []) => {
                if (start > end) return range
                const next = addDays(start, 1)
                return dateRange(next, end, [...range, start])
            }

            const range = dateRange(new Date(this.startDate), new Date(this.endDate))

            return range.map(date => date.toISOString().slice(0, 10))
        }

        weekDay(date) {
            return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(date).getDay()]
        }

        dayNumber(date) {
            return new Date(date).getUTCDate()
        }

        isSelectedTariffMissing(tariffId) {
            return !!tariffId && !this.tariffs.some(tariff => tariff.id === tariffId)
        }

        isRoomClosedInPeriod(serviceId, dateFrom, dateTo) {
            return supplierStore.availabilityServices.some(
                availability =>
                    availability.serviceId === serviceId &&
                    Date.parse(availability.dateFrom) <= Date.parse(dateTo) &&
                    Date.parse(availability.dateTo) >= Date.parse(dateFrom) &&
                    (availability.blockout || availability.status === 'BLOCKED')
            )
        }

        async applyAvailability(rq, tariffId) {
            const dayOfWeeks = rq.dayOfWeek
            const targetTariffs = tariffId ? this.tariffs.filter(tariff => tariff.id === tariffId) : this.tariffs

            const dailyRates = targetTariffs.map(tariff => {
                const tariffRq = clone(rq)

                Object.assign(tariffRq, {
                    tariffId: tariff.id,
                    amount: null,
                    dayOfWeeks: dayOfWeeks,
                    active: tariffRq.operation !== 'BLOCK',
                })

                delete tariffRq.categoryId
                delete tariffRq.dayOfWeek
                delete tariffRq.operation

                return tariffRq
            })

            if (dailyRates.length) {
                await this.$api.dailyRates.put({dailyRates})
            }

            if (!tariffId) {
                await this.$api.availability.put(this.contractId, [rq])
            }
        }

        async updateAvailabilities({tariffId = null, ...rq}) {
            if (this.isSelectedTariffMissing(tariffId)) {
                this.$toast.error(this.$t('error_message.save_error'))
                return
            }

            try {
                await this.applyAvailability(rq, tariffId)

                this.$toast.success(this.$t('supplier.successfully_updated'))

                await timeout(300)
                await this.load()
            } catch (error) {
                this.$toast.error(error?.errors?.[0]?.message || this.$t('error_message.save_error'))
            }
        }

        async updateDailyRates(rq) {
            try {
                let status = rq.operation !== 'BLOCK'

                const dailyRate = {...rq, active: status}
                delete dailyRate.operation

                await this.$api.dailyRates.put({dailyRates: [dailyRate]})

                this.$toast.success(this.$t('supplier.successfully_updated'))

                await this.load()
            } catch (error) {
                this.$toast.error(error?.errors?.[0]?.message || this.$t('error_message.save_error'))
            }
        }

        async updateMultipleAvailabilities(data) {
            const checkedServices = data.services.filter(service => service.checked !== false)

            if (!checkedServices.length) return

            const servicesInApplyOrder =
                data.operation === 'BLOCK'
                    ? checkedServices
                    : [
                          ...checkedServices.filter(service => !this.isSpecialRoomService(service.serviceId)),
                          ...checkedServices.filter(service => this.isSpecialRoomService(service.serviceId)),
                      ]

            const tariffId = data.tariffId ?? null

            if (this.isSelectedTariffMissing(tariffId)) {
                this.$toast.error(this.$t('error_message.save_error'))
                return
            }

            try {
                for (const service of servicesInApplyOrder) {
                    let rq = {
                        ...service,
                        operation: data.operation,
                        dateFrom: data.dateFrom,
                        dateTo: data.dateTo,
                        dayOfWeek: data.dayOfWeek,
                    }
                    delete rq.checked
                    await this.applyAvailability(rq, tariffId)
                    await timeout(100)
                }

                this.$toast.success(this.$t('supplier.successfully_updated'))

                await timeout(300)
                await this.load()

                this.warnIfRoomsStillClosed(data, checkedServices, tariffId)
            } catch (error) {
                this.$toast.error(error?.errors?.[0]?.message || this.$t('error_message.save_error'))

                await this.load()
            }
        }

        warnIfRoomsStillClosed(data, services, tariffId) {
            if (!tariffId || data.operation !== 'CLEAR_BLOCK') return

            const stillClosed = services.some(service =>
                this.isRoomClosedInPeriod(service.serviceId, data.dateFrom, data.dateTo)
            )

            if (stillClosed) {
                this.$toast.warning(this.$t('supplier.tariff_opened_room_still_closed'))
            }
        }

        get contract() {
            return supplierStore.contract
        }

        get breadCrumbText() {
            return this.productName || '...'
        }

        get productName() {
            return supplierStore.contract?.name || ''
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isOwnHotel() {
            return this.contract?.serviceType === 'ownHotel'
        }

        get secondLevelInfoTabs() {
            switch (this.contract?.serviceType) {
                case contractServiceType.ownHotel:
                    return ['info', 'room_types', 'meal_types', 'extra_services', 'guests']
                case contractServiceType.ownCharter:
                    return ['info', 'flight_number', 'schedule', 'extra_services', 'travel_categories']
                case contractServiceType.ownExcursion:
                    return ['info', 'catalogue', 'time_table', 'extra_services', 'travel_categories']
                case contractServiceType.concert:
                    return ['info', 'catalogue', 'services', 'extra_services', 'travel_categories']
                case contractServiceType.ownTransfer:
                    if (supplierStore.fullContract?.transferType === 'SHARED') {
                        return ['info', 'catalogue', 'transfer_schedule', 'extra_services', 'travel_categories']
                    }
                    return ['info', 'catalogue', 'transfer_schedule', 'extra_services']
                case contractServiceType.ownCarRent:
                    return ['info', 'drivers', 'extra_services']
                case contractServiceType.ownCruise:
                    return ['info', 'catalogue', 'accommodation', 'board_types', 'extra_services', 'guests']
                case contractServiceType.tour:
                    return ['info', 'catalogue', 'accommodation', 'board_types', 'extra_services', 'guests']
                case contractServiceType.ownExtraService:
                    return [
                        'info',
                        'services',
                        'extra_services',
                        ...(supplierStore.fullContract?.unitPricingType === 'TOURIST' ? ['travel_categories'] : []),
                    ]
                case contractServiceType.customComplexTour:
                    if (supplierStore.fullContract?.priceConfiguration === 'STATIC') {
                        return [
                            'info',
                            'itinerary',
                            'itinerary_description',
                            'catalogue',
                            'accommodation',
                            'board_types',
                            'guests',
                        ]
                    }
                    return ['info', 'itinerary', 'itinerary_description']
                case contractServiceType.ownInsurance:
                    return ['info', 'coverage_options', 'guests']
                case contractServiceType.ownVisa:
                    return ['info', 'services', 'duration', 'extra_services', 'guests', 'terms_of_ordering']
                default:
                    return ['info']
            }
        }
    }

    export default ContractPage
</script>

<style scoped lang="scss">
    :deep(div) {
        table.contract-availability-table {
            margin: 0 auto;
        }
        table.contract-availability-table td {
            min-width: 100px;
            max-width: 100px;
            width: 100px;
            height: 40px !important;
            cursor: pointer;
            padding: 2px;
        }

        table.contract-availability-table td,
        table.contract-availability-table th {
            border: 1px solid #dee2e6;
        }

        table.contract-availability-table td,
        table.contract-availability-table th {
            vertical-align: middle;
        }

        table.contract-availability-table td:nth-child(1) {
            padding-left: 5px;
            font-size: 14px;
        }

        .date-cell {
            font-size: 12px;
            font-weight: normal;
            font-style: normal;
            font-stretch: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: center;
            color: #9b9b9b;
        }

        .date-cell-day {
            font-size: 20px;
            font-weight: 400;
            color: #1c1c1c;
            font-style: normal;
            font-stretch: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: center;
        }

        .service-name-row {
            height: 35px;
            font-size: 20px;
            font-weight: bold;
            font-style: normal;
            font-stretch: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: left;
            color: #000a12;
        }

        .service-name-row th {
            padding-left: 5px;
        }

        .td-header {
            position: relative;
            background: white;
            min-height: 77px;
            vertical-align: middle;
            min-width: 160px;
        }

        .v-text-field {
            padding-top: 0 !important;
        }

        table.contract-availability-table .v-text-field,
        table.contract-availability-table .v-input {
            max-width: 100px !important;
            width: 100px !important;
        }

        table.contract-availability-table .v-field {
            max-width: 100px !important;
            width: 100px !important;
        }

        table.contract-availability-table .v-field__input {
            max-width: 100px !important;
            width: 100px !important;
            min-width: 100px !important;
        }

        table.contract-availability-table .v-input input {
            text-align: center;
            max-width: 100px !important;
            width: 100px !important;
        }

        table.contract-availability-table input::-webkit-outer-spin-button,
        table.contract-availability-table input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        table.contract-availability-table input[type='number'] {
            -moz-appearance: textfield;
        }

        /* Basic room group styling */
        .basic-room-group-wrapper {
            border: 2px solid #e0e0e0;
            border-radius: 12px;
            margin-bottom: 32px;
            padding: 20px;
            background-color: #fafafa;
            overflow-x: auto;
        }

        .sustainability-level-section {
            margin-bottom: 20px;
            margin-top: 16px;
            border-radius: 8px;
            border-left: 4px solid;
            position: relative;
            /* Use reasonable width for background that covers table + padding */
            margin-left: -20px;
            margin-right: -20px;
            padding: 16px 20px;
            border: 1px solid;
            /* Ensure background extends full width of content */
            width: calc(100% + 40px);
            min-width: max-content;
        }

        .sustainability-level-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 0;
            border-radius: 8px;
        }

        .sustainability-level-section > * {
            position: relative;
            z-index: 1;
        }

        .basic-section {
            border-left-color: #2196f3;
            border-color: #e3f2fd;
        }

        .basic-section::before {
            background-color: #f3f9ff;
        }

        .sustainable-section {
            border-left-color: #4caf50;
            border-color: #e8f5e8;
        }

        .sustainable-section::before {
            background-color: #f1f8e9;
        }

        .zero-section {
            border-left-color: #ff9800;
            border-color: #fff3e0;
        }

        .zero-section::before {
            background-color: #fff3e0;
        }

        .sustainability-level-section table {
            margin: 0;
            min-width: max-content; /* Ensure table doesn't shrink below content width */
        }

        .sustainability-level-section h4 {
            margin: 0 0 16px 0;
            padding: 0 0 0 8px;
        }

        .sustainability-subtitle {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 12px 0;
            padding-left: 8px;
        }

        .basic-section .sustainability-subtitle {
            color: #1976d2;
        }

        .sustainable-section .sustainability-subtitle {
            color: #388e3c;
        }

        .zero-section .sustainability-subtitle {
            color: #f57c00;
        }
    }
</style>
